import {
  ShapeUtil,
  Vec,
  IndexKey,
  getIndexBetween,
  createBindingId,
  TLShapePartial,
  TLResizeInfo,
  resizeBox,
  TLBaseShape,
  Rectangle2d,
  TLShapeUtilCanBindOpts,
  TLUnknownShape,
  createShapePropsMigrationSequence,
  createShapePropsMigrationIds,
  createShapeId,
  TLShape,
  TLShapeId,
  atom,
  TLArrowBinding,
} from "tldraw";

import {
  PAGE_REGION_INSET,
  allBoundChildren,
  isPageRegionChild,
} from "@/components/canvas-editor/bindings/layout";
import {
  ColorVariant,
  getHasHiddenParent,
} from "@/components/canvas-editor/components/Container";
import { withPageRefitHeld } from "@/components/canvas-editor/layers";
import {
  BaseShapeProps,
  customNodeType,
  FeedProps,
  FeedShape,
  feedType,
  paramCompareType,
  isFeedShape,
  LayoutBinding,
  LogicShape,
  Padding,
  targetPlaceholderType,
  isEachShape,
  eachType,
  logicType,
  customNodeRootType,
  isCustomNodeRootShape,
} from "@/components/canvas-editor/shapes/shapes";

export const dropTargetId = atom<string | undefined>("name", undefined);

const targetPlaceholderId = createShapeId("placeholder");

let startPositionCache: Record<TLShapeId, { x: number; y: number }> = {};

export function isBindableShape(shape: TLUnknownShape): shape is BindableShape {
  return "h" in shape.props && "w" in shape.props && "minW" in shape.props;
}

export function createMigrations(type: string) {
  const versions = createShapePropsMigrationIds(type, {
    RemoveMinH: 1,
    insensitive: 2,
    loginInNode: 3,
    starSyntax: 4,
    defaultHeight: 5,
    timeWindows: 6,
  });

  return createShapePropsMigrationSequence({
    sequence: [
      {
        id: versions.RemoveMinH,
        up(props) {
          delete (props as { minH?: number }).minH;
        },
      },
      {
        id: versions.insensitive,
        up(props) {
          if ("caseSensitive" in props) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            props.insensitive = !props.caseSensitive;

            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            delete props.caseSensitive;
          }
        },
        down(props) {
          if ("insensitive" in props) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            props.caseSensitive = !props.insensitive;

            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            delete props.insensitive;
          }
        },
      },
      {
        id: versions.loginInNode,
        up(props: Record<string, unknown>) {
          if ("type" in props && props.type === "feed") {
            (props as unknown as FeedProps).padding.top = 84;
          }
        },
        down(props: Record<string, unknown>) {
          if ("type" in props && props.type === "feed") {
            (props as unknown as FeedProps).padding.top = 44;
          }
        },
      },
      {
        id: versions.starSyntax,
        up(props) {
          if ("var" in props) {
            const varValue = (props as unknown as { var: string }).var;
            const newProps = props as unknown as { var: string };

            switch (varValue) {
              case "embed.images[0].alt":
              case "embed.images[1].alt":
                newProps.var = "embed.images[*].alt";
                break;
              case "langs[0]":
                newProps.var = "langs[*]";
                break;
              case "labels.values[0].val":
                newProps.var = "labels.values[*].val";
                break;
            }
          }
        },
        down(props) {
          if ("var" in props) {
            const varValue = (props as unknown as { var: string }).var;
            const newProps = props as unknown as { var: string };

            switch (varValue) {
              case "embed.images[*].alt":
                newProps.var = "embed.images[0].alt";
                break;
              case "langs[*]":
                newProps.var = "langs[0]";
                break;
              case "labels.values[*].val":
                newProps.var = "labels.values[0].val";
                break;
            }
          }
        },
      },
      {
        id: versions.defaultHeight,
        up(props: Record<string, unknown>) {
          if ("h" in props && props.h === 84) {
            (props as unknown as FeedProps).h = 92;
          }
        },
        down(props: Record<string, unknown>) {
          if ("h" in props && props.h === 92) {
            (props as unknown as FeedProps).h = 88;
          }
        },
      },
      {
        // Time of Day and Date Range grew from ONE window to a list. Stored canvases
        // (tldraw snapshots) still carry the single-window props, and this is what
        // lets them load. The guards are specific to those two shapes: nothing else
        // in shapes/ has `startHour`/`endHour`, or `start` + `end` beside
        // `utcOffsetMinutes`.
        id: versions.timeWindows,
        up(props: Record<string, unknown>) {
          if ("windows" in props) return;
          if ("startHour" in props && "endHour" in props) {
            props.windows = [
              { start: String(props.startHour), end: String(props.endHour) },
            ];
            delete props.startHour;
            delete props.endHour;
          } else if (
            "start" in props &&
            "end" in props &&
            "utcOffsetMinutes" in props
          ) {
            props.windows = [
              { start: String(props.start), end: String(props.end) },
            ];
            delete props.start;
            delete props.end;
          }
        },
        down(props: Record<string, unknown>) {
          if (!Array.isArray(props.windows)) return;
          const first = (
            props.windows as Array<{ start: string; end: string }>
          )[0] ?? { start: "", end: "" };
          // `type` is not on props, so the two nodes are told apart by what a
          // window holds: an hour is a bare integer, a date bound never is.
          if (/^\d{1,2}$/.test(first.start) && /^\d{1,2}$/.test(first.end)) {
            props.startHour = first.start;
            props.endHour = first.end;
          } else {
            props.start = first.start;
            props.end = first.end;
          }
          delete props.windows;
        },
      },
    ],
  });
}

export type BindableShape = TLBaseShape<string, BaseShapeProps>;

export const isReadOnlyEditorPage = atom("is-readonly-editor", false);

export abstract class BindableUtil<
  Shape extends BindableShape = BindableShape,
> extends ShapeUtil<Shape> {
  public isBindable = true;

  private isBoundToParentId(shape: TLShape, parentId: TLShapeId): boolean {
    const bindings = this.editor.getBindingsToShape<LayoutBinding>(
      shape,
      "layout",
    );

    if (bindings.length > 0) {
      const firstBinding = bindings[0];

      if (firstBinding && firstBinding.fromId === parentId) {
        return true;
      }

      const parent = firstBinding
        ? this.editor.getShape(firstBinding.fromId)
        : undefined;

      if (!parent) {
        return false;
      }

      return this.isBoundToParentId(parent, parentId);
    }

    return false;
  }

  /**
   * The page point a drop resolves against, for THIS shape.
   *
   * 🔴 DRAGGING A MULTI-SELECTION USED TO DROP ALL BUT ONE SHAPE. `onTranslate`
   * and `onTranslateEnd` are per-shape hooks, and both resolved their target
   * from `editor.inputs.currentPagePoint` — the cursor, which is one point no
   * matter how many shapes are moving. So every selected shape asked "what is
   * under the cursor, and where in it do I go?", got the SAME container and the
   * SAME insertion index, and they overwrote each other down to one survivor.
   * `pointOffset` was the parameter meant to separate them and nothing has ever
   * passed it.
   *
   * With more than one shape moving, each takes its own bounds centre instead.
   * That is where the user actually dropped it, so the shapes land in the
   * arrangement they were dragged in, and each gets its own index because
   * `getBindingIndexForPosition` sorts siblings by y.
   *
   * Reported 2026-09-21: two nodes selected, dragged into a group, only one
   * landed.
   *
   * The single-shape path is deliberately byte-identical to before — the cursor
   * is the right anchor when there is one shape, and it is what every existing
   * drag has been tuned against.
   */
  private dropAnchorFor(shape: Shape, pointOffset?: Vec) {
    if (pointOffset) {
      return Vec.Add(this.editor.inputs.currentPagePoint, pointOffset);
    }
    // `getSelectedShapeIds` is what tldraw translates as a group; a drag of an
    // unselected shape selects it first, so this is 1 for an ordinary drag.
    if (this.editor.getSelectedShapeIds().length <= 1) {
      return this.editor.inputs.currentPagePoint;
    }
    const bounds = this.editor.getShapePageBounds(shape);
    return bounds ? bounds.center : this.editor.inputs.currentPagePoint;
  }

  getTargetContainer(shape: Shape, pageAnchor: Vec) {
    // Find the container shape that the element is being dropped on
    return this.editor.getShapeAtPoint(pageAnchor, {
      hitInside: true,
      filter: (otherShape) => {
        return (
          otherShape.id !== shape.id &&
          otherShape.type !== targetPlaceholderType &&
          otherShape.type !== customNodeType &&
          otherShape.type !== paramCompareType &&
          !this.isBoundToParentId(otherShape, shape.id) &&
          this.editor.canBindShapes({
            fromShape: otherShape,
            toShape: shape,
            binding: "layout",
          })
        );
      },
    }) as LogicShape | FeedShape | undefined;
  }

  private getExistingBinding(shape: Shape) {
    // Find the center of the element shape
    const pageAnchor = this.editor.inputs.currentPagePoint;

    // Find the container shape that the element is being dropped on
    const targetContainer = this.getTargetContainer(shape, pageAnchor);

    if (!targetContainer) return;

    const existingBinding = this.editor
      .getBindingsFromShape<LayoutBinding>(targetContainer, "layout")
      .find((b) => b.toId === shape.id);

    return existingBinding;
  }

  override canResize(shape: Shape) {
    return !this.getExistingBinding(shape);
  }

  getMinWidth(shape: Shape): number {
    const bindings = this.editor.getBindingsFromShape(shape, "layout");
    let childrenMinW = 0;

    if (bindings.length > 0) {
      const boundShapes = bindings.map((b) => this.editor.getShape(b.toId)!);

      childrenMinW = boundShapes.reduce((acc, shape) => {
        if ("minW" in shape.props && typeof shape.props.minW === "number") {
          const childMinW = this.editor
            .getShapeUtil<BindableUtil>(shape)
            .getMinWidth(shape as BindableShape);

          if (childMinW > acc) {
            return childMinW;
          }
        }

        return acc;
      }, 0);
    }

    const shapeMinW = shape.props.minW || 0;

    return Math.max(shapeMinW, childrenMinW);
  }

  private getPositionInPage(shape: TLShape): { x: number; y: number } {
    const binding = this.editor.getBindingsToShape<LayoutBinding>(
      shape,
      "layout",
    );

    if (!binding.length) {
      return shape;
    }

    const parent = binding[0]
      ? this.editor.getShape(binding[0].fromId)
      : undefined;

    if (!parent) {
      return shape;
    }

    const parentPosition = this.getPositionInPage(parent);

    return {
      x: parentPosition.x + shape.x,
      y: parentPosition.y + shape.y,
    };
  }

  getBindingIndexForPosition(
    shape: Shape,
    container: LogicShape | FeedShape,
    pageAnchor: Vec,
  ) {
    // All the layout bindings from the container -- within the SAME section
    // when the container is the Feed, so a layer is ordered among layers and
    // never interleaved with the rules root.
    const region = isPageRegionChild(shape);
    const allBindings = this.editor
      .getBindingsFromShape<LayoutBinding>(container, "layout")
      .filter((b) => {
        if (!isFeedShape(container)) return true;
        const s = this.editor.getShape(b.toId);
        return s ? isPageRegionChild(s) === region : true;
      })
      .sort((a, b) => (a.props.index > b.props.index ? 1 : -1));

    // Sort the shape by y position
    const siblingShapes = allBindings
      .map((b) => this.editor.getShape(b.toId)!)
      .map((s) => {
        let h = (s.props as { h: number }).h;

        const binding = this.editor.getBindingsToShape<LayoutBinding>(
          shape,
          "layout",
        );

        let { y } = this.getPositionInPage(s);

        if (binding[0]?.props.dimensions && s.id === shape.id) {
          // The shape is being translated, so we the y position is calculated
          // from the shapes placeholder binding.
          y = y - shape.y + binding[0].props.dimensions.y;
          h = binding[0].props.dimensions.h;
        }

        return { id: s.id, y, h };
      })
      .sort((a, b) => a.y - b.y);

    const indexPositions: number[] = [];

    // Find all the insert positions for the shape
    for (let i = 0; i < siblingShapes.length; i++) {
      const sibling = siblingShapes[i];

      if (!sibling) {
        continue;
      }

      indexPositions.push(sibling.y);

      if (i === siblingShapes.length - 1) {
        indexPositions.push(sibling.y + sibling.h);
      }
    }

    // Find the closes insert position
    let distance = Infinity;
    let order = 0;

    indexPositions.forEach((p, i) => {
      const dist = Math.abs(p - pageAnchor.y);

      if (dist < distance) {
        distance = dist;
        order = i;
      }
    });

    // Get a fractional index between the two siblings
    const belowSib = allBindings[order - 1];
    const aboveSib = allBindings[order];
    let index: IndexKey;

    if (belowSib?.toId === shape.id) {
      index = belowSib.props.index;
    } else if (aboveSib?.toId === shape.id) {
      index = aboveSib.props.index;
    } else {
      index = getIndexBetween(belowSib?.props.index, aboveSib?.props.index);
    }

    return { index, order, siblingShapes };
  }

  abstract getType(): string;
  static docs: {
    title: string;
    description: React.ReactNode;
    defaultColor: ColorVariant;
  };

  updateProps(shape: Shape, newProps: Partial<Shape["props"]>) {
    const children = [shape, ...allBoundChildren(this.editor, shape)];
    const arrowBindings = children.flatMap((child) =>
      this.editor.getBindingsToShape<TLArrowBinding>(child, "arrow"),
    );
    const newHeight = newProps.h || shape.props.h;
    const heighDelta = newHeight - shape.props.h;

    for (const arrow of arrowBindings) {
      const oldShapePosition = Vec.MulV(
        arrow.props.normalizedAnchor,
        new Vec(shape.props.w, shape.props.h),
      );

      if (
        oldShapePosition.y < newHeight &&
        (heighDelta < 0 ||
          arrow.props.normalizedAnchor.y * newHeight < shape.props.h)
      ) {
        // If its within the shape translate it such that it doesn't move
        // the normalized vector is relative to the shape's height so we need math
        this.editor.updateBinding({
          id: arrow.id,
          type: "arrow",
          fromId: arrow.fromId,
          toId: arrow.toId,
          props: {
            isExact: arrow.props.isExact,
            isPrecise: arrow.props.isPrecise,
            terminal: arrow.props.terminal,
            normalizedAnchor: {
              x: arrow.props.normalizedAnchor.x,
              y: oldShapePosition.y / newHeight,
            },
          },
        });
      }

      const arrowBindingsFrom = this.editor.getBindingsFromShape(
        arrow.fromId,
        "arrow",
      );
      const otherBinding = arrowBindingsFrom.find((b) => b.toId !== shape.id);
      if (!otherBinding) continue;
      const otherShape = this.editor.getShape(otherBinding.toId);
      if (!otherShape) continue;

      // Otherwise the shape is bound ot a position that is outside the new shape
      // so we just move the bound shape
      this.editor.updateShape({
        id: otherShape.id,
        type: otherShape.type,
        x: otherShape.x,
        y: otherShape.y + heighDelta,
      });

      this.translateAllArrowShapes(otherShape, 0, heighDelta);
    }

    this.editor.updateShape({
      id: shape.id,
      type: this.getType(),
      props: {
        ...shape.props,
        ...newProps,
        h: newProps.h ?? shape.props.h,
        w: newProps.w ?? shape.props.w,
      } as unknown as TLShapePartial<Shape>,
    });
  }

  override canBind({
    fromShapeType,
    toShapeType,
    bindingType,
  }: TLShapeUtilCanBindOpts<Shape>) {
    if (bindingType === "arrow") {
      return true;
    }

    return (
      (fromShapeType === logicType ||
        fromShapeType === feedType ||
        fromShapeType === customNodeRootType ||
        fromShapeType === customNodeType ||
        fromShapeType === eachType) &&
      toShapeType === this.getType() &&
      bindingType === "layout"
    );
  }

  override canEdit() {
    return false;
  }
  override hideRotateHandle() {
    return true;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  override onResize(shape: Shape, info: TLResizeInfo<any>): any {
    info.scaleY = 1;

    const afterResize = resizeBox(shape, info) as Shape;
    const padding =
      "padding" in shape.props
        ? (shape.props.padding as Padding)
        : { left: 0, right: 0, top: 0, bottom: 0 };

    afterResize.props.w = Math.max(
      afterResize.props.w,
      this.getMinWidth(shape) + padding.left + padding.right,
    );

    return afterResize;
  }

  private shouldExitTranslate(shape: TLShape) {
    if ("isReadOnly" in shape.props && shape.props.isReadOnly) return true;
    if (isReadOnlyEditorPage.get()) return true;
  }

  override onTranslateStart(shape: Shape) {
    if (this.shouldExitTranslate(shape)) {
      return;
    }

    startPositionCache = {};
    // Update all the layout bindings for this shape to be placeholders
    this.editor.updateBindings(
      this.editor
        .getBindingsToShape<LayoutBinding>(shape, "layout")
        .map((binding) => ({
          ...binding,
          props: {
            ...binding.props,
            placeholder: true,
            dimensions: {
              w: shape.props.w,
              h: shape.props.h,
              x: shape.x,
              y: shape.y,
            },
          },
        })),
    );

    const boundsShapes = allBoundChildren(this.editor, shape);
    this.editor.bringToFront([shape, ...boundsShapes]);
  }

  private translateArrowShapes(shape: TLShape, deltaX: number, deltaY: number) {
    const arrows = this.editor.getBindingsToShape(shape, "arrow");

    for (const arrow of arrows) {
      if (arrow.type !== "arrow") continue;

      const arrowBindings = this.editor.getBindingsFromShape(
        arrow.fromId,
        "arrow",
      );
      const otherShapeId = arrowBindings.find((b) => b.toId !== shape.id)?.toId;

      if (!otherShapeId) continue;

      const otherShape = this.editor.getShape(otherShapeId);

      if (!otherShape) continue;

      if (!startPositionCache[otherShape.id]) {
        startPositionCache[otherShape.id] = {
          x: otherShape.x,
          y: otherShape.y,
        };
      }

      this.editor.updateShape({
        id: otherShape.id,
        type: otherShape.type,
        x: (startPositionCache[otherShape.id]?.x ?? 0) + deltaX,
        y: (startPositionCache[otherShape.id]?.y ?? 0) + deltaY,
      });
    }
  }

  translateAllArrowShapes(
    shape: TLShape,
    deltaX: number,
    deltaY: number,
    reset = false,
  ) {
    if (reset) {
      startPositionCache = {};
    }

    const children = [shape, ...allBoundChildren(this.editor, shape)];

    for (const child of children) {
      this.translateArrowShapes(child, deltaX, deltaY);
    }
  }

  override onTranslate(prevShape: Shape, shape: Shape, pointOffset?: Vec) {
    if (this.shouldExitTranslate(shape)) {
      return;
    }

    const deltaX = shape.x - prevShape.x;
    const deltaY = shape.y - prevShape.y;

    this.translateAllArrowShapes(shape, deltaX, deltaY);

    // Find the container shape that the element is being dropped on
    const pageAnchor = this.dropAnchorFor(shape, pointOffset);
    const targetContainer = this.getTargetContainer(shape, pageAnchor);

    // Delete all the bindings to the element
    if (!targetContainer || !this.canPlaceInShape(targetContainer, shape)) {
      this.editor.deleteShape(targetPlaceholderId);
      dropTargetId.set(undefined);
      return;
    } else {
      dropTargetId.set(targetContainer.id);
    }

    // Get the index for the new binding
    const { order, siblingShapes } = this.getBindingIndexForPosition(
      shape,
      targetContainer,
      pageAnchor,
    );

    // Containers with ONE slot draw no indicator: the custom-node root, and
    // the Feed's rules section. The Feed's Page section is a stack of layers
    // and shows where the dragged layer will land, like any other stack.
    if (
      (targetContainer.type as string) === customNodeRootType ||
      ((targetContainer.type as string) === feedType && !isPageRegionChild(shape))
    ) {
      return;
    }

    // Show the target
    let target = this.editor.getShape(targetPlaceholderId);

    if (!target) {
      this.editor.createShape({
        id: targetPlaceholderId,
        type: targetPlaceholderType,
        x: 0,
        y: 0,
      });
      target = this.editor.getShape(targetPlaceholderId);
    }

    if (!target) {
      throw new Error("Wasn't able to create target placeholder");
    }

    // Calculate the y positions to place the indicator
    let cumulativeHeight = 0;

    for (let i = 0; i < order; i++) {
      const shape = siblingShapes[i];
      if (!shape) continue;
      cumulativeHeight += (shape.h || 0) + targetContainer.props.padding.gap;
    }

    const inset = 8;
    const parentPosition = this.getPositionInPage(targetContainer);
    // Inside the Feed a layer sits one inset in from each side of the Page
    // box; the indicator takes the same inset so it reads as one of them.
    const regionX = isFeedShape(targetContainer) ? PAGE_REGION_INSET : 0;
    const regionW = isFeedShape(targetContainer) ? -2 * PAGE_REGION_INSET : 0;
    const offset = new Vec(
      targetContainer.props.padding.left + regionX - inset / 2,
      targetContainer.props.padding.top + cumulativeHeight - 9,
    );
    this.editor.updateShape({
      id: target.id,
      type: target.type,
      x: parentPosition.x + offset.x,
      y: parentPosition.y + offset.y,
      props: {
        w:
          targetContainer.props.w -
          targetContainer.props.padding.left -
          targetContainer.props.padding.right +
          regionW +
          inset,
      },
    });
    // Above the layers it slots between, or it is hidden behind them.
    this.editor.bringToFront([target.id]);
  }

  canPlaceInShape(container: TLShape, shape: TLShape) {
    if (isFeedShape(container)) {
      // The Feed holds a Page section of up to ten layers and ONE rules root.
      // Which section a shape belongs to is decided by its type.
      const siblings = this.editor
        .getBindingsFromShape<LayoutBinding>(container, "layout")
        .filter((b) => b.toId !== shape.id)
        .map((b) => this.editor.getShape(b.toId))
        .filter((s): s is TLShape => Boolean(s));
      if (isPageRegionChild(shape)) {
        if (siblings.filter(isPageRegionChild).length >= 10) return false;
        // One gym layer per feed: two would be two experiments on one page,
        // which the allocator cannot attribute. The tool refuses to make a
        // second; this refuses to drop one back in beside another.
        const isGym = (s: TLShape) => Boolean((s.props as { gym?: boolean }).gym);
        if (isGym(shape) && siblings.some((s) => isPageRegionChild(s) && isGym(s))) return false;
      } else if (siblings.some((s) => !isPageRegionChild(s))) {
        return false;
      }
    } else if (isEachShape(container) || isCustomNodeRootShape(container)) {
      const bindings = this.editor
        .getBindingsFromShape<LayoutBinding>(container, "layout")
        .filter((b) => b.toId !== shape.id);

      if (bindings.length > 0) {
        return false;
      }
    }

    if (this.shouldExitTranslate(container)) {
      return false;
    }

    if (container.type === "custom-node") {
      return false;
    }

    return true;
  }

  override onTranslateEnd(_: Shape, shape: Shape, pointOffset?: Vec) {
    // Dropping (or nudging) a bound block unbinds it and binds it again, and
    // tldraw completes each write in between as its own operation — sixteen
    // for one nudge. The page's share refit (CanvasEditor) is held for the
    // whole of it and runs once at the end, over the page as it ends up; run
    // per completion it refit the OTHER layers alone to 100% first and a nudge
    // turned [0.5, 0.5] into [0.66, 0.34] (live, 9/16).
    this.editor.run(() =>
      withPageRefitHeld(this.editor, () => this.finishTranslate(shape, pointOffset)),
    );
  }

  private finishTranslate(shape: Shape, pointOffset?: Vec) {
    if (this.shouldExitTranslate(shape)) {
      return;
    }

    dropTargetId.set(undefined);
    this.editor.deleteShape(targetPlaceholderId);

    // Find the container shape that the element is being dropped on
    const pageAnchor = this.dropAnchorFor(shape, pointOffset);
    const targetContainer = this.getTargetContainer(shape, pageAnchor);

    // No target container? no problem
    if (!targetContainer) {
      const bindings = this.editor.getBindingsToShape<LayoutBinding>(
        shape,
        "layout",
      );

      // If there are other bindings to the element, delete them
      for (const binding of bindings) {
        this.cleanUpBindings([binding]);
      }

      return;
    }

    if (targetContainer && !this.canPlaceInShape(targetContainer, shape)) {
      return;
    }

    // get the index for the new binding
    let index: IndexKey | undefined = this.getBindingIndexForPosition(
      shape,
      targetContainer,
      pageAnchor,
    ).index;

    // delete all the previous bindings for this shape
    this.cleanUpBindings(
      this.editor.getBindingsToShape<LayoutBinding>(shape, "layout"),
    );

    let fromId = targetContainer.id;

    // if we're trying to bind an each node to a feed we need to first create a logic node
    if (
      (targetContainer.type as string) === customNodeRootType &&
      shape.type === eachType
    ) {
      const logicId = createShapeId();

      this.editor.createShape({
        id: logicId,
        type: logicType,
        x: shape.x,
        y: shape.y,
        props: {
          type: "or",
        },
      });

      this.editor.createBinding<LayoutBinding>({
        id: createBindingId(),
        type: "layout",
        fromId,
        toId: logicId,
        props: {
          index,
          placeholder: false,
        },
      });
      this.editor.updateShape({
        id: logicId,
        type: logicType,
        parentId: fromId,
      });

      fromId = logicId;
      index = undefined;
    }

    // Shrink placed nodes into container
    if (shape.props.minW) {
      this.editor.updateShape({
        id: shape.id,
        type: shape.type,
        props: {
          w: shape.props.minW,
        },
      });
    }

    // ...and then create a new one
    this.editor.createBinding<LayoutBinding>({
      id: createBindingId(),
      type: "layout",
      fromId,
      toId: shape.id,
      props: {
        ...(index ? { index } : {}),
        placeholder: false,
      },
    });

    this.editor.updateShape({
      id: shape.id,
      type: shape.type,
      parentId: fromId,
    });
  }

  private cleanUpBindings(bindings: LayoutBinding[]) {
    for (const binding of bindings) {
      this.editor.deleteBinding(binding);

      const shape = this.editor.getShape(binding.toId);

      if (shape) {
        this.editor.reparentShapes([shape], this.editor.getCurrentPageId());
        const boundsShapes = allBoundChildren(this.editor, shape);
        this.editor.bringToFront(boundsShapes);
      }
    }
  }

  getBoundToFeed(shape: Shape) {
    let binding = this.editor.getBindingsToShape<LayoutBinding>(
      shape,
      "layout",
    )[0];

    while (binding) {
      if (binding.props.placeholder) {
        return false;
      }

      const parent = this.editor.getShape(binding.fromId);

      if (!parent) {
        break;
      }

      if (parent && (isFeedShape(parent) || isCustomNodeRootShape(parent))) {
        return true;
      }

      binding = this.editor.getBindingsToShape<LayoutBinding>(
        parent,
        "layout",
      )[0];
    }

    return false;
  }

  override getGeometry(shape: Shape) {
    if (getHasHiddenParent(this.editor, shape)) {
      return new Rectangle2d({
        width: 0,
        height: 0,
        isFilled: false,
      });
    }

    return new Rectangle2d({
      width: Math.max(shape.props.minW || 0, shape.props.w),
      height: shape.props.h,
      isFilled: true,
    });
  }

  indicator(shape: Shape) {
    return (
      <rect
        width={Math.max(shape.props.minW || 0, shape.props.w)}
        height={shape.props.h}
      />
    );
  }
}
